/**
 * Per-question review list, shared by the quiz result screen and the scorecard's
 * re-opened past attempts.
 *
 * For a wrong answer the correct answer is always spelled out and labelled, rather
 * than left for the learner to infer from an unlabelled line.
 */

import { Link } from 'react-router-dom'
import type { QuizResultItem } from '../api'

/** Modes whose answer text is Japanese and needs the JP font stack. */
const JP_ANSWER_MODES = ['component', 'kanji_from_meaning']

const MODE_LABELS: Record<string, string> = {
  meaning: 'Meaning',
  reading: 'Reading',
  component: 'Component',
  kanji_from_meaning: 'Kanji from meaning',
  mixed: 'Mixed',
}

export default function QuizReview({
  items,
  level,
}: {
  items: QuizResultItem[]
  level?: string | null
}) {
  const wrong = items.filter((i) => !i.correct).length

  return (
    <section className="card overflow-hidden">
      <header className="flex items-center justify-between gap-3 border-b border-ink-700/60 px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Review</h2>
        <span className="text-xs text-ink-500">
          {wrong === 0 ? 'all correct' : `${wrong} to revisit`}
        </span>
      </header>

      <ol className="divide-y divide-ink-700/40">
        {items.map((item) => {
          const jp = JP_ANSWER_MODES.includes(item.prompt)
          return (
            <li key={item.position} className="flex items-start gap-4 px-5 py-3">
              <span
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  item.correct ? 'bg-brand-500/20 text-brand-500' : 'bg-rose-500/20 text-rose-300'
                }`}
                title={item.correct ? 'correct' : 'incorrect'}
              >
                {item.correct ? '✓' : '✗'}
              </span>

              <Link
                to={`/map?${level ? `level=${level}&` : ''}sel=${encodeURIComponent(item.char)}&focus=${encodeURIComponent(item.char)}`}
                title={`Open ${item.char} on the map`}
                className="jp w-11 shrink-0 text-3xl leading-none text-slate-100 transition hover:text-brand-400"
              >
                {item.char}
              </Link>

              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wide text-ink-500">
                  {MODE_LABELS[item.prompt] ?? item.prompt}
                </div>

                {item.correct ? (
                  <div className={`truncate text-sm text-slate-200 ${jp ? 'jp' : ''}`}>{item.expected}</div>
                ) : (
                  <div className="mt-0.5 space-y-0.5">
                    <div className="flex flex-wrap items-baseline gap-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-500">
                        Correct answer
                      </span>
                      <span className={`text-sm font-semibold text-brand-400 ${jp ? 'jp text-lg leading-tight' : ''}`}>
                        {item.expected}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-rose-400/80">
                        You answered
                      </span>
                      <span className={`text-sm text-rose-300/90 ${jp ? 'jp text-lg leading-tight' : ''}`}>
                        {item.answer?.trim() ? item.answer : 'nothing'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <span className="mt-0.5 shrink-0 font-mono text-[11px] text-ink-500">
                {(item.elapsed_ms / 1000).toFixed(1)}s
              </span>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
